/**
 * AppraisalContextStrip — Iter172.
 *
 * High-density Client + Property summary strip that sits above the
 * tab strip on AppraisalDetailPage. Visible on every sub-tab
 * (Overview / Roof Appraisal / Diagrams / Builders / Components /
 * Roof Details / Quote Calculation / Quote Items / Contractors /
 * Quality) automatically because they all share the same parent page.
 *
 * Per spec (iter172):
 *   - Client column: name + address + ID, plus an ✎ link to the
 *     /clients/{id} edit form in a NEW TAB.
 *   - Property column: address + ID + total-properties-for-this-client
 *     count + a dropdown of every OTHER property the client owns.
 *     Picking one opens /properties/{sibling_id} in a NEW TAB
 *     (sibling navigator — never mutates the current appraisal).
 *   - ⚙ Fields picker (mirrors RoofDetailsQuickBar) — the operator
 *     can hide / show any field. Choice persists in localStorage
 *     under `rtr.appraisal.context_strip.visible_fields.v1`.
 *   - Strip itself collapses (chevron + localStorage flag) for
 *     screens where the operator wants more vertical space.
 *
 * Props:
 *   - api: axios instance (required)
 *   - client: client doc from /api/appraisals/{id} embed (may be {})
 *   - property: property doc from same embed (may be {})
 *   - data-testid='appraisal-context-strip'
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  User, MapPin, Edit3, ChevronDown, ChevronUp, Settings,
  ExternalLink, Building2, Loader2, AlertTriangle, Search, X,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { fireRoofDetailsChanged } from '@/utils/roofEvents';
import IdChip from '@/components/IdChip';

const COLLAPSE_KEY = 'rtr.appraisal.context_strip.collapsed.v1';
const VISIBLE_KEY  = 'rtr.appraisal.context_strip.visible_fields.v1';

// Field catalogue + sensible defaults. Operators can ⚙-toggle any of
// these. Keep keys stable — they're saved in localStorage and a rename
// would silently break existing preferences.
const FIELD_CATALOGUE = [
  { key: 'client_name',      label: 'Client name',      group: 'client' },
  { key: 'client_address',   label: 'Client address',   group: 'client' },
  { key: 'client_id',        label: 'Client ID',        group: 'client' },
  { key: 'client_email',     label: 'Client email',     group: 'client' },
  { key: 'client_phone',     label: 'Client phone',     group: 'client' },
  { key: 'property_address', label: 'Property address', group: 'property' },
  { key: 'property_id',      label: 'Property ID',      group: 'property' },
  { key: 'property_total',   label: 'Properties total', group: 'property' },
  { key: 'property_sibling', label: 'Other properties dropdown', group: 'property' },
];

const DEFAULT_VISIBLE = [
  'client_name', 'client_address', 'client_id',
  'property_address', 'property_id', 'property_total', 'property_sibling',
];

const formatClientAddress = (c = {}) => {
  const num    = (c.address_num    || '').trim();
  const street = (c.address_street || '').trim();
  const suburb = (c.address_suburb || '').trim();
  const city   = (c.address_city   || '').trim();
  const line1  = [num, street].filter(Boolean).join(' ').trim();
  const line2  = [suburb, city].filter(Boolean).join(', ').trim();
  return [line1, line2].filter(Boolean).join(', ');
};

const formatPropertyAddress = (p = {}) => {
  const num    = (p.property_address_num    || '').trim();
  const street = (p.property_address_street || '').trim();
  const suburb = (p.property_address_suburb || '').trim();
  const line1  = [num, street].filter(Boolean).join(' ').trim();
  return [line1, suburb].filter(Boolean).join(', ');
};

const clientDisplayName = (c = {}) =>
  c.is_company && c.company_name
    ? c.company_name
    : `${c.name_first || ''} ${c.name_last || ''}`.trim() || '(unnamed)';

// Iter174 — inline orphan re-link popover. When an appraisal's
// client_id / property_id points to a record that no longer exists
// (or was never embedded properly), the operator can search-and-pick
// a replacement right here without leaving the appraisal.
//
// Iter176 — also reused as the empty-state CTA when there's NO link
// at all (effClientId === '' / effPropertyId === ''). The component
// auto-detects:
//   - orphanId truthy  → "Linked … not found" warning + Re-link CTA
//   - orphanId empty   → "No … linked yet" copy + Link/Create CTA
// And exposes a Create-new tab for clients (quick name + optional
// company form → POST /clients → auto-link).
function OrphanRelink({ kind, orphanId, api, appraisalId, appraisal, onLinked, clientIdForCreate = '' }) {
  const isOrphan = !!orphanId;   // truthy → re-link flow, falsy → first-link flow
  const [mode, setMode] = useState('idle'); // idle | search | create
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [newClient, setNewClient] = useState({ name_first: '', name_last: '', company_name: '' });
  // Iter196 — inline property create. Mirrors the client flow with the
  // minimal-viable fields (street number, street name, suburb, city).
  // The new property is auto-linked to `clientIdForCreate` so future
  // /properties/{id} edits land under the correct owner. If the
  // appraisal has no client_id yet (rare empty-state) the create
  // button stays disabled — link a client first.
  const [newProp, setNewProp] = useState({
    property_address_num: '',
    property_address_street: '',
    property_address_suburb: '',
    property_address_city: 'Hamilton',
  });

  // Debounced search against /clients or /properties.
  useEffect(() => {
    if (mode !== 'search' || !query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const path = kind === 'client'
          ? `/clients?search=${encodeURIComponent(query)}&limit=8`
          : `/properties?search=${encodeURIComponent(query)}&limit=8`;
        const { data } = await api.get(path);
        setResults(data?.items || []);
      } finally { setSearching(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [api, kind, query, mode]);

  const linkId = useCallback(async (newId, doc) => {
    if (!newId || !appraisalId) return;
    setLinking(true);
    try {
      // Wave 2 H9 fix (2026-02) — TRUE partial PUT: only the id we're
      // re-linking. Backend's `exclude_unset=True` preserves the other
      // half of the client / property link and everything else.
      const patch = kind === 'client'
        ? { client_id: newId }
        : { property_id: newId };
      await api.put(`/appraisals/${appraisalId}`, patch);
      const label = kind === 'client'
        ? (doc?.company_name || `${doc?.name_first || ''} ${doc?.name_last || ''}`.trim() || 'client')
        : `${doc?.property_address_num || ''} ${doc?.property_address_street || ''}`.trim() || 'property';
      toast.success(`${isOrphan ? 'Re-linked' : 'Linked'} to ${label}`);
      fireRoofDetailsChanged();
      onLinked?.();
      setMode('idle');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to link');
    } finally {
      setLinking(false);
    }
  }, [api, kind, appraisalId, appraisal, onLinked, isOrphan]);

  const handleCreateClient = useCallback(async () => {
    const trimmed = {
      name_first: newClient.name_first.trim(),
      name_last: newClient.name_last.trim(),
      company_name: newClient.company_name.trim(),
    };
    if (!trimmed.name_first && !trimmed.name_last && !trimmed.company_name) {
      toast.error('Enter a name or company');
      return;
    }
    setLinking(true);
    try {
      const { data } = await api.post('/clients', {
        ...trimmed,
        is_company: !!trimmed.company_name && !trimmed.name_first && !trimmed.name_last,
      });
      await linkId(data._id, data);
      setNewClient({ name_first: '', name_last: '', company_name: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create client');
      setLinking(false);
    }
  }, [api, newClient, linkId]);

  const handleCreateProperty = useCallback(async () => {
    const trimmed = {
      property_address_num:    newProp.property_address_num.trim(),
      property_address_street: newProp.property_address_street.trim(),
      property_address_suburb: newProp.property_address_suburb.trim(),
      property_address_city:   newProp.property_address_city.trim(),
    };
    if (!trimmed.property_address_street) {
      toast.error('Street name is required');
      return;
    }
    if (!clientIdForCreate) {
      toast.error('Link a client first — a property must belong to a client');
      return;
    }
    setLinking(true);
    try {
      const { data } = await api.post('/properties', {
        ...trimmed,
        client_id: clientIdForCreate,
      });
      await linkId(data._id, data);
      setNewProp({ property_address_num: '', property_address_street: '', property_address_suburb: '', property_address_city: 'Hamilton' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create property');
      setLinking(false);
    }
  }, [api, newProp, clientIdForCreate, linkId]);

  // Header copy + colours flip between orphan (amber alarm) and empty
  // (slate informational) states.
  const tone = isOrphan ? 'amber' : 'slate';
  const tip = isOrphan
    ? `Linked ${kind} not found`
    : `No ${kind} linked yet — saves won't persist until you link one`;

  return (
    <div data-testid={`orphan-relink-${kind}`} className="space-y-1.5">
      <div className={`flex items-start gap-2 px-2 py-1.5 ${
        tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-slate-50 border-slate-200 text-slate-700'
      } border rounded text-[11px]`}>
        <AlertTriangle size={12} className={`flex-shrink-0 mt-0.5 ${tone === 'amber' ? 'text-amber-600' : 'text-slate-500'}`} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold">{tip}</div>
          {isOrphan && (
            <div className="font-mono-num text-[10px] text-amber-700 truncate" title={orphanId}>
              id: {orphanId}
            </div>
          )}
        </div>
      </div>

      {mode === 'idle' && (
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            data-testid={`orphan-relink-${kind}-btn`}
            onClick={() => setMode('search')}
            disabled={!appraisalId}
            className="flex-1 text-[10px] font-bold inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-cyan-300 text-cyan-800 bg-white hover:bg-cyan-50 disabled:opacity-40"
          >
            <Search size={10} /> {isOrphan ? 'Re-link' : 'Find existing'}
          </button>
          {kind === 'client' && (
            <button
              type="button"
              data-testid={`orphan-relink-${kind}-create-btn`}
              onClick={() => setMode('create')}
              disabled={!appraisalId}
              className="flex-1 text-[10px] font-bold inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-cyan-300 text-cyan-800 bg-white hover:bg-cyan-50 disabled:opacity-40"
            >
              <Edit3 size={10} /> Create new
            </button>
          )}
          {kind === 'property' && (
            <button
              type="button"
              data-testid={`orphan-relink-${kind}-create-btn`}
              onClick={() => setMode('create')}
              disabled={!appraisalId || !clientIdForCreate}
              title={!clientIdForCreate ? 'Link a client first — a property must belong to a client' : 'Create a new property and link it'}
              className="flex-1 text-[10px] font-bold inline-flex items-center justify-center gap-1 px-2 py-1 rounded border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50 disabled:opacity-40"
            >
              <Edit3 size={10} /> Create new
            </button>
          )}
        </div>
      )}

      {mode === 'search' && (
        <div className="space-y-1 border border-slate-200 rounded p-1.5 bg-white">
          <div className="relative">
            <input
              autoFocus
              data-testid={`orphan-relink-${kind}-input`}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={kind === 'client' ? 'Search client name / company…' : 'Search property address…'}
              className="w-full text-xs px-2 py-1 border border-slate-200 rounded"
            />
            <button
              type="button"
              onClick={() => { setMode('idle'); setQuery(''); setResults([]); }}
              className="absolute right-1 top-1 text-slate-400 hover:text-slate-600"
              title="Cancel"
            >
              <X size={11} />
            </button>
            {searching && <Loader2 size={10} className="absolute right-6 top-1.5 animate-spin text-cyan-500" />}
          </div>
          {results.length > 0 && (
            <div className="border border-slate-100 rounded max-h-44 overflow-y-auto bg-white">
              {results.map(r => {
                const label = kind === 'client'
                  ? `${r.company_name || `${r.name_first || ''} ${r.name_last || ''}`.trim() || '(unnamed)'}${r.access_id ? `  ·  C${r.access_id}` : ''}`
                  : `${r.property_address_num || ''} ${r.property_address_street || ''}, ${r.property_address_suburb || ''}`.replace(/^,\s*/, '').trim() || `(P${r.access_id || r._id.slice(-4)})`;
                return (
                  <button
                    key={r._id}
                    type="button"
                    data-testid={`orphan-relink-${kind}-result-${r._id}`}
                    disabled={linking}
                    onClick={() => linkId(r._id, r)}
                    className="w-full text-left text-[11px] px-2 py-1 hover:bg-cyan-50 border-b border-slate-50 last:border-0 truncate disabled:opacity-50"
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {query && !searching && results.length === 0 && (
            <p className="text-[10px] italic text-slate-500 px-1">No matches for “{query}”.</p>
          )}
        </div>
      )}

      {mode === 'create' && kind === 'client' && (
        <div className="space-y-1.5 border border-slate-200 rounded p-1.5 bg-white" data-testid="orphan-relink-client-create-body">
          <div className="grid grid-cols-2 gap-1">
            <input
              data-testid="orphan-relink-client-create-first"
              value={newClient.name_first}
              onChange={e => setNewClient(s => ({ ...s, name_first: e.target.value }))}
              placeholder="First name"
              className="text-xs px-2 py-1 border border-slate-200 rounded"
            />
            <input
              data-testid="orphan-relink-client-create-last"
              value={newClient.name_last}
              onChange={e => setNewClient(s => ({ ...s, name_last: e.target.value }))}
              placeholder="Last name"
              className="text-xs px-2 py-1 border border-slate-200 rounded"
            />
          </div>
          <input
            data-testid="orphan-relink-client-create-company"
            value={newClient.company_name}
            onChange={e => setNewClient(s => ({ ...s, company_name: e.target.value }))}
            placeholder="Company (optional)"
            className="w-full text-xs px-2 py-1 border border-slate-200 rounded"
          />
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              data-testid="orphan-relink-client-create-submit"
              onClick={handleCreateClient}
              disabled={linking}
              className="flex-1 text-[10px] font-bold inline-flex items-center justify-center gap-1 px-2 py-1 rounded bg-cyan-700 text-white hover:bg-cyan-800 disabled:opacity-40"
            >
              {linking ? <Loader2 size={10} className="animate-spin" /> : <Edit3 size={10} />}
              Create & link
            </button>
            <button
              type="button"
              onClick={() => { setMode('idle'); setNewClient({ name_first: '', name_last: '', company_name: '' }); }}
              className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
          <p className="text-[9px] text-slate-400">
            Open <strong>Edit Client</strong> after creating to fill in address, phones, etc.
          </p>
        </div>
      )}

      {mode === 'create' && kind === 'property' && (
        <div className="space-y-1.5 border border-slate-200 rounded p-1.5 bg-white" data-testid="orphan-relink-property-create-body">
          <div className="grid grid-cols-3 gap-1">
            <input
              data-testid="orphan-relink-property-create-num"
              value={newProp.property_address_num}
              onChange={e => setNewProp(s => ({ ...s, property_address_num: e.target.value }))}
              placeholder="No."
              className="text-xs px-2 py-1 border border-slate-200 rounded"
            />
            <input
              data-testid="orphan-relink-property-create-street"
              value={newProp.property_address_street}
              onChange={e => setNewProp(s => ({ ...s, property_address_street: e.target.value }))}
              placeholder="Street name *"
              className="col-span-2 text-xs px-2 py-1 border border-slate-200 rounded"
            />
          </div>
          <div className="grid grid-cols-2 gap-1">
            <input
              data-testid="orphan-relink-property-create-suburb"
              value={newProp.property_address_suburb}
              onChange={e => setNewProp(s => ({ ...s, property_address_suburb: e.target.value }))}
              placeholder="Suburb"
              className="text-xs px-2 py-1 border border-slate-200 rounded"
            />
            <input
              data-testid="orphan-relink-property-create-city"
              value={newProp.property_address_city}
              onChange={e => setNewProp(s => ({ ...s, property_address_city: e.target.value }))}
              placeholder="City"
              className="text-xs px-2 py-1 border border-slate-200 rounded"
            />
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              data-testid="orphan-relink-property-create-submit"
              onClick={handleCreateProperty}
              disabled={linking || !clientIdForCreate}
              className="flex-1 text-[10px] font-bold inline-flex items-center justify-center gap-1 px-2 py-1 rounded bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-40"
            >
              {linking ? <Loader2 size={10} className="animate-spin" /> : <Edit3 size={10} />}
              Create & link
            </button>
            <button
              type="button"
              onClick={() => { setMode('idle'); }}
              className="text-[10px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
          <p className="text-[9px] text-slate-400">
            Will be linked to the appraisal's client. Open <strong>Edit Property</strong> after creating to fill in roof details, agent, tenant, etc.
          </p>
        </div>
      )}
    </div>
  );
}

export default function AppraisalContextStrip({ api, appraisalId = '', appraisal = null, client = {}, property = {}, clientId = '', propertyId = '', onLinked = null }) {
  // Iter173 — fall back to the appraisal's top-level client_id /
  // property_id when the embedded client / property docs are empty
  // (orphaned references, partial data, brand-new linkages). Without
  // this fallback the Edit links silently disappear even though the
  // operator clearly has a linked record. The `effective*Id` values
  // are used everywhere the embed's _id was used before.
  const effClientId   = client?._id   || clientId   || '';
  const effPropertyId = property?._id || propertyId || '';
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch (_) { return false; }
  });
  const [visibleFields, setVisibleFields] = useState(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr) && arr.every(k => typeof k === 'string')) return arr;
    } catch (_) { /* ignore */ }
    return DEFAULT_VISIBLE;
  });
  useEffect(() => {
    try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(visibleFields)); } catch (_) { /* ignore */ }
  }, [visibleFields]);

  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [siblings, setSiblings] = useState(null); // null = not loaded, [] = loaded but none
  const [loadingSiblings, setLoadingSiblings] = useState(false);

  // Lazy-load every property for this client so we can show
  // "X properties total" + the dropdown of siblings. Skipped when
  // the strip is collapsed or the operator hid both fields.
  const needSiblings =
    !collapsed &&
    !!effClientId &&
    (visibleFields.includes('property_total') || visibleFields.includes('property_sibling'));

  useEffect(() => {
    if (!needSiblings) return;
    let alive = true;
    setLoadingSiblings(true);
    api.get(`/properties?client_id=${effClientId}&limit=200`)
      .then(({ data }) => { if (alive) setSiblings(data?.items || []); })
      .catch(() => { if (alive) setSiblings([]); })
      .finally(() => { if (alive) setLoadingSiblings(false); });
    return () => { alive = false; };
  }, [api, effClientId, needSiblings]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch (_) { /* ignore */ }
      return next;
    });
  }, []);

  const isVis = (k) => visibleFields.includes(k);

  // Header is rendered whether or not we have data — operator can
  // still ⚙ or collapse.
  // Iter176 — the strip stays mounted even when both client_id AND
  // property_id are missing so the operator can use the inline CTAs
  // to create / find both. Previously this returned null which left
  // brand-new appraisals with nowhere to link from.
  // We still bail out if there's literally no appraisal context at
  // all (no appraisalId — i.e. used on a page that doesn't pass it).
  if (!appraisalId && !effClientId && !effPropertyId) return null;

  // Iter174 — orphan detection. When an appraisal carries a
  // client_id / property_id but the embedded sub-doc is empty (record
  // deleted / orphan reference / stale denormalisation), surface a
  // clear "⚠ Linked client ID xxx not found" warning + Re-link CTA
  // instead of the misleading "(unnamed)" placeholder.
  // Iter176 — also surface a CTA when there's NO link at all so the
  // operator can search-or-create a client without leaving the
  // appraisal. Both flows reuse OrphanRelink (different tone).
  const isClientOrphan =
    !!effClientId &&
    !client?.name_first && !client?.name_last && !client?.company_name && !client?.access_id;
  const isClientMissing = !effClientId;
  const isPropertyOrphan =
    !!effPropertyId &&
    !property?.property_address_street && !property?.access_id;
  const isPropertyMissing = !effPropertyId;

  const clientAddr   = formatClientAddress(client);
  const propAddr     = formatPropertyAddress(property);
  const otherProps   = (siblings || []).filter(p => p._id !== effPropertyId);
  const totalCount   = siblings ? siblings.length : null;
  const onPickSibling = (e) => {
    const id = e.target.value;
    if (!id) return;
    // Per spec: open the chosen property in a NEW TAB; never mutate
    // the appraisal's property_id.
    window.open(`/properties/${id}`, '_blank', 'noopener,noreferrer');
    e.target.value = ''; // reset so the same option can be picked twice
  };

  return (
    <div
      data-testid="appraisal-context-strip"
      className="border border-slate-200 rounded-md bg-white shadow-sm"
    >
      {/* Header row — always visible */}
      <div className="flex items-center justify-between gap-2 px-3 py-1.5 border-b border-slate-100">
        <button
          type="button"
          onClick={toggleCollapsed}
          data-testid="appraisal-context-strip-toggle"
          className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 hover:text-slate-900"
        >
          {collapsed ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
          Client & Property
        </button>
        <button
          type="button"
          onClick={() => setFieldsOpen(true)}
          data-testid="appraisal-context-strip-fields-btn"
          className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-slate-500 hover:text-cyan-700 hover:bg-slate-50"
          title="Choose which fields are visible in this strip"
        >
          <Settings size={11} /> Fields
        </button>
      </div>

      {!collapsed && (
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-100">
          {/* CLIENT COLUMN */}
          <div className="p-3 space-y-1" data-testid="context-strip-client-col">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-cyan-700">
                <User size={11} /> Client
                {/* Iter211 — Client ID moved inline right of the title */}
                {isVis('client_id') && (client?.access_id || client?.client_no) && (
                  <IdChip
                    type="client"
                    value={client.access_id || client.client_no}
                    className="ml-1"
                    testId="context-strip-client-id"
                  />
                )}
              </span>
              {effClientId && (
                <a
                  href={`/clients/${effClientId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="context-strip-client-edit"
                  className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded border border-cyan-300 text-cyan-800 bg-white hover:bg-cyan-50"
                  title="Open Client edit form in a new tab"
                >
                  <Edit3 size={11} /> Edit Client <ExternalLink size={10} />
                </a>
              )}
            </div>
            {isClientOrphan || isClientMissing ? (
              <OrphanRelink
                kind="client"
                orphanId={isClientOrphan ? effClientId : ''}
                api={api}
                appraisalId={appraisalId}
                appraisal={appraisal}
                onLinked={onLinked}
              />
            ) : (
              <>
                {isVis('client_name') && (
                  effClientId ? (
                    <a
                      href={`/clients/${effClientId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="context-strip-client-name"
                      className="block text-sm font-semibold text-slate-800 hover:text-cyan-700 hover:underline truncate"
                      title="Open Client edit form in a new tab"
                    >
                      {clientDisplayName(client)}
                    </a>
                  ) : (
                    <div className="text-sm font-semibold text-slate-800 truncate" data-testid="context-strip-client-name">
                      {clientDisplayName(client)}
                    </div>
                  )
                )}
                {isVis('client_address') && clientAddr && (
                  <div className="text-xs text-slate-600 truncate" data-testid="context-strip-client-address">
                    {clientAddr}
                  </div>
                )}
              </>
            )}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 pt-0.5">
              {/* Iter211 — Client ID chip moved inline next to the column title above. */}
              {isVis('client_email') && client?.email && (
                <a
                  href={`mailto:${client.email}`}
                  className="text-[10px] text-slate-500 hover:text-[#D35400] truncate"
                  title={client.email}
                >
                  {client.email}
                </a>
              )}
              {isVis('client_phone') && client?.phone && (
                <a
                  href={`tel:${client.phone}`}
                  className="text-[10px] text-slate-500 hover:text-[#D35400]"
                  title={client.phone}
                >
                  {client.phone}
                </a>
              )}
            </div>
          </div>

          {/* PROPERTY COLUMN */}
          <div className="p-3 space-y-1" data-testid="context-strip-property-col">
            <div className="flex items-center justify-between gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                <MapPin size={11} /> Property
                {/* Iter211 — Property ID moved inline right of the title */}
                {isVis('property_id') && (property?.access_id || property?.property_no) && (
                  <IdChip
                    type="property"
                    value={property.access_id || property.property_no}
                    className="ml-1"
                    testId="context-strip-property-id"
                  />
                )}
              </span>
              {effPropertyId && (
                <a
                  href={`/properties/${effPropertyId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="context-strip-property-edit"
                  className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded border border-emerald-300 text-emerald-800 bg-white hover:bg-emerald-50"
                  title="Open Property edit form in a new tab"
                >
                  <Edit3 size={11} /> Edit Property <ExternalLink size={10} />
                </a>
              )}
            </div>
            {isPropertyOrphan || isPropertyMissing ? (
              <OrphanRelink
                kind="property"
                orphanId={isPropertyOrphan ? effPropertyId : ''}
                api={api}
                appraisalId={appraisalId}
                appraisal={appraisal}
                onLinked={onLinked}
                clientIdForCreate={effClientId}
              />
            ) : (
              <>
                {isVis('property_address') && (
                  effPropertyId ? (
                    <a
                      href={`/properties/${effPropertyId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="context-strip-property-address"
                      className="block text-sm font-semibold text-slate-800 hover:text-emerald-700 hover:underline truncate"
                      title="Open Property edit form in a new tab"
                    >
                      {propAddr || '(no address)'}
                    </a>
                  ) : (
                    <div className="text-sm font-semibold text-slate-800 truncate" data-testid="context-strip-property-address">
                      {propAddr || '(no address)'}
                    </div>
                  )
                )}
              </>
            )}
            <div className="flex items-center flex-wrap gap-x-2 gap-y-1 pt-0.5">
              {/* Iter211 — Property ID chip moved inline next to the column title above. */}
              {isVis('property_total') && (
                <span
                  data-testid="context-strip-property-total"
                  className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-px inline-flex items-center gap-1"
                  title="Total properties for this client"
                >
                  <Building2 size={9} />
                  {loadingSiblings && totalCount == null
                    ? <Loader2 size={9} className="animate-spin" />
                    : `${totalCount ?? '—'} total`}
                </span>
              )}
            </div>
            {isVis('property_sibling') && otherProps.length > 0 && (
              <select
                data-testid="context-strip-property-sibling-picker"
                onChange={onPickSibling}
                defaultValue=""
                className="mt-1 w-full text-xs px-2 py-1 border border-slate-200 rounded bg-white text-slate-600 hover:border-emerald-300"
                title="Open another property owned by this client in a new tab"
              >
                <option value="">Other properties ({otherProps.length}) — open in new tab…</option>
                {otherProps.map(p => (
                  <option key={p._id} value={p._id}>
                    {formatPropertyAddress(p) || `(P${p.access_id || p._id.slice(-4)})`}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      )}

      {/* ⚙ Fields picker modal — pattern mirrors RoofDetailsQuickBar */}
      <Dialog open={fieldsOpen} onOpenChange={setFieldsOpen}>
        <DialogContent data-testid="context-strip-fields-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base">Strip — visible fields</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Tick the Client / Property fields that should stay visible on the strip. Your preference is remembered across appraisals.
          </p>
          <div className="grid grid-cols-2 gap-1.5 max-h-80 overflow-auto">
            {FIELD_CATALOGUE.map(f => {
              const checked = visibleFields.includes(f.key);
              return (
                <label key={f.key} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-slate-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`context-strip-fields-toggle-${f.key}`}
                    checked={checked}
                    onChange={(e) => {
                      setVisibleFields(cur => {
                        if (e.target.checked) return [...cur.filter(k => k !== f.key), f.key];
                        return cur.filter(k => k !== f.key);
                      });
                    }}
                    className="rounded text-cyan-700"
                  />
                  <span className="text-slate-700">{f.label}</span>
                  <span className="ml-auto text-[9px] uppercase tracking-wide text-slate-400">{f.group}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              data-testid="context-strip-fields-reset"
              onClick={() => setVisibleFields(DEFAULT_VISIBLE)}
            >
              Reset to defaults
            </Button>
            <Button size="sm" onClick={() => setFieldsOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
